import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, money, formatApiError } from "../../lib/api";
import { Plus, Edit, Trash2, Tag, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "side-mirror", label: "Side Mirror" },
  { value: "windshield", label: "Windshield" },
  { value: "backglass", label: "Backglass" },
];

const inputCls = "w-full h-10 px-3 text-sm border border-zinc-300 rounded-sm bg-white focus:border-zinc-900 focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5 block";

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // coupon obj or {} for new
  const [products, setProducts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data.coupons);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/admin/products?limit=500").then(({ data }) => setProducts(data.products)).catch(() => {});
  }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Coupon deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (c) => {
    try { await api.put(`/admin/coupons/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout
      title="Coupons & discounts"
      actions={
        <button onClick={() => setEditing({})} data-testid="coupon-new-btn" className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 h-10 text-sm tracking-wide rounded-sm">
          <Plus size={14} /> New coupon
        </button>
      }
    >
      <div className="bg-white border border-zinc-200 rounded-md">
        <table className="w-full text-sm" data-testid="coupons-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-3 py-3">Discount</th>
              <th className="text-left px-3 py-3">Scope</th>
              <th className="text-left px-3 py-3">Window</th>
              <th className="text-right px-3 py-3">Usage</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-zinc-500">Loading…</td></tr>}
            {!loading && coupons.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-zinc-500">No coupons yet. Create one to offer a discount.</td></tr>}
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`coupon-row-${c.code}`}>
                <td className="px-4 py-3">
                  <span className="font-mono font-bold">{c.code}</span>
                  {c.description && <div className="text-xs text-zinc-500">{c.description}</div>}
                </td>
                <td className="px-3 py-3 font-mono">{c.discount_type === "percentage" ? `${c.discount_value}%` : money(c.discount_value)}{c.min_order_amount ? <span className="block text-[10px] text-zinc-400">min {money(c.min_order_amount)}</span> : null}</td>
                <td className="px-3 py-3 text-zinc-600 capitalize">{c.scope}{c.scope === "categories" ? ` (${(c.categories || []).length})` : c.scope === "products" ? ` (${(c.product_ids || []).length})` : ""}</td>
                <td className="px-3 py-3 text-xs text-zinc-500">{(c.start_date || "").slice(0, 10) || "—"} → {(c.end_date || "").slice(0, 10) || "∞"}</td>
                <td className="px-3 py-3 text-right font-mono">{c.usage_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                <td className="px-3 py-3">
                  <button onClick={() => toggleActive(c)} data-testid={`coupon-toggle-${c.code}`} className={`text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => setEditing(c)} data-testid={`coupon-edit-${c.code}`} className="inline-flex items-center gap-1 px-2.5 h-8 text-xs border border-zinc-200 rounded hover:bg-zinc-100 text-zinc-700"><Edit size={13} /> Edit</button>
                    <button onClick={() => remove(c)} data-testid={`coupon-delete-${c.code}`} className="inline-flex items-center justify-center w-8 h-8 border border-zinc-200 rounded hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CouponModal
          coupon={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AdminLayout>
  );
}

function CouponModal({ coupon, products, onClose, onSaved }) {
  const isNew = !coupon.id;
  const [f, setF] = useState({
    code: coupon.code || "",
    description: coupon.description || "",
    discount_type: coupon.discount_type || "percentage",
    discount_value: coupon.discount_value ?? "",
    scope: coupon.scope || "all",
    product_ids: coupon.product_ids || [],
    categories: coupon.categories || [],
    min_order_amount: coupon.min_order_amount ?? "",
    start_date: (coupon.start_date || "").slice(0, 10),
    end_date: (coupon.end_date || "").slice(0, 10),
    usage_limit: coupon.usage_limit ?? "",
    active: coupon.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [psearch, setPsearch] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggleCategory = (c) => set("categories", f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c]);
  const toggleProduct = (id) => set("product_ids", f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        code: f.code.trim(),
        description: f.description.trim() || null,
        discount_type: f.discount_type,
        discount_value: parseFloat(f.discount_value),
        scope: f.scope,
        product_ids: f.scope === "products" ? f.product_ids : [],
        categories: f.scope === "categories" ? f.categories : [],
        min_order_amount: f.min_order_amount === "" ? null : parseFloat(f.min_order_amount),
        start_date: f.start_date || null,
        end_date: f.end_date || null,
        usage_limit: f.usage_limit === "" ? null : parseInt(f.usage_limit, 10),
        active: f.active,
      };
      if (isNew) await api.post("/admin/coupons", payload);
      else await api.put(`/admin/coupons/${coupon.id}`, payload);
      toast.success(isNew ? "Coupon created" : "Coupon updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const filteredProducts = products.filter((p) => !psearch || `${p.name} ${p.sku}`.toLowerCase().includes(psearch.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="coupon-modal">
      <div className="bg-white rounded-md w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-display text-lg flex items-center gap-2"><Tag size={18} /> {isNew ? "New coupon" : `Edit ${coupon.code}`}</h2>
          <button onClick={onClose} data-testid="coupon-modal-close" className="p-2 hover:bg-zinc-100 rounded"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Code</label>
            <input data-testid="coupon-code-input" value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="SAVE10" className={inputCls + " font-mono uppercase"} />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select data-testid="coupon-active-select" value={f.active ? "1" : "0"} onChange={(e) => set("active", e.target.value === "1")} className={inputCls}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Description</label>
            <input data-testid="coupon-desc-input" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. 10% off all mirrors" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Discount type</label>
            <select data-testid="coupon-type-select" value={f.discount_type} onChange={(e) => set("discount_type", e.target.value)} className={inputCls}>
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed amount ($)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Value</label>
            <input data-testid="coupon-value-input" type="number" min="0" step="0.01" value={f.discount_value} onChange={(e) => set("discount_value", e.target.value)} placeholder={f.discount_type === "percentage" ? "10" : "25.00"} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Minimum order amount ($)</label>
            <input data-testid="coupon-min-input" type="number" min="0" step="0.01" value={f.min_order_amount} onChange={(e) => set("min_order_amount", e.target.value)} placeholder="optional" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Usage limit</label>
            <input data-testid="coupon-limit-input" type="number" min="1" value={f.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} placeholder="unlimited" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Start date</label>
            <input data-testid="coupon-start-input" type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End date</label>
            <input data-testid="coupon-end-input" type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} />
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Applies to</label>
            <select data-testid="coupon-scope-select" value={f.scope} onChange={(e) => set("scope", e.target.value)} className={inputCls}>
              <option value="all">All products</option>
              <option value="categories">Selected categories</option>
              <option value="products">Selected products</option>
            </select>
          </div>

          {f.scope === "categories" && (
            <div className="md:col-span-2" data-testid="coupon-categories">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c.value} type="button" onClick={() => toggleCategory(c.value)} data-testid={`coupon-cat-${c.value}`}
                    className={`px-3 h-9 text-sm rounded-sm border ${f.categories.includes(c.value) ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {f.scope === "products" && (
            <div className="md:col-span-2" data-testid="coupon-products">
              <input value={psearch} onChange={(e) => setPsearch(e.target.value)} placeholder="Search products…" className={inputCls + " mb-2"} />
              <div className="border border-zinc-200 rounded-md max-h-52 overflow-y-auto">
                {filteredProducts.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer border-b border-zinc-100 last:border-0">
                    <input type="checkbox" checked={f.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} className="w-4 h-4" />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="font-mono text-xs text-zinc-400">{p.sku}</span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-zinc-500 mt-1">{f.product_ids.length} selected</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200">
          <button onClick={onClose} className="px-4 h-10 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="coupon-save-btn" className="px-5 h-10 text-sm bg-red-600 hover:bg-red-700 text-white rounded-sm disabled:opacity-50">
            {busy ? "Saving…" : isNew ? "Create coupon" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
